import { useQuery } from '@tanstack/react-query';
import { Network } from '@wren/shared';
import { callout } from '../../../style/article';

interface CalloutReceipt {
  documentType: string;
  date: string;
  amountSent: number;
  documentUrl: string;
}

function formatType(type: string) {
  return type
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(
    new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  );
}

function formatAmount(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

// Fetches this project's most recent published receipts. Returns [] on any error
// (e.g. a 403 before public receipts are enabled) so the callout falls back to its
// static copy instead of breaking the project page.
async function getRecentReceipts(slug: string): Promise<CalloutReceipt[]> {
  const [response, body] = await Network.get<{ receipts: CalloutReceipt[] }>(
    'receipts',
    { slug, limit: '3' }
  );
  if (!response.ok || !body) return [];
  return body.receipts ?? [];
}

export default function ReceiptsAndDocuments({ slug }: { slug: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['projectReceipts', slug],
    queryFn: () => getRecentReceipts(slug),
    // Guard against an empty slug, which the backend would treat as "no filter"
    // and return unrelated projects' most-recent receipts.
    enabled: Boolean(slug),
    staleTime: 300000, // 5 minutes
    retry: false,
  });
  const recent = data ?? [];

  return (
    <div css={callout}>
      <h3 id="receipts-and-documents">Receipts and documents</h3>{' '}
      <p>
        We keep a public record of all payment receipts we&apos;ve received from
        our project partners.
      </p>
      {recent.length > 0 && (
        <ul className="mb-3 mt-0 flex list-none flex-col gap-1 pl-0">
          {recent.map((receipt) => (
            <li
              key={receipt.documentUrl}
              className="flex items-baseline justify-between gap-3 border-0 border-b border-solid border-gray-8 py-1.5 last:border-b-0"
            >
              <a
                className="text-wren-green no-underline hover:underline"
                href={receipt.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {formatType(receipt.documentType)} · {formatDate(receipt.date)}
              </a>
              <span className="whitespace-nowrap tabular-nums text-gray-3">
                {formatAmount(receipt.amountSent)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* Hold the link line until the fetch resolves so a project that has
          receipts doesn't briefly flash the generic fallback copy. */}
      {!isLoading && (
        <p>
          {recent.length > 0 ? (
            <a href={`/receipts?project=${encodeURIComponent(slug)}`}>
              View all receipts for this project →
            </a>
          ) : (
            <>
              You can browse the <a href="/receipts">receipt ledger here</a>.
            </>
          )}
        </p>
      )}
    </div>
  );
}
